// client/app/components/Regions/Port/ServiceProviders/FormFields.jsx
import React, { useEffect, useState } from 'react';
import { Field, FieldArray } from 'redux-form';
import { TextField, SelectField } from 'redux-form-material-ui';
import { RaisedButton, MenuItem, IconButton, DatePicker } from 'material-ui';
import ControlPointIcon from 'material-ui/svg-icons/image/control-point';
import ContentRemoveIcon from 'material-ui/svg-icons/content/remove-circle-outline';
import CompaniesAutoComplete from '../../../common/CompaniesAutoComplete';
import Emails from './Emails';
import Phones from './Phones';
import portActivities from 'data/portActivities';
import { length, required, numericality } from 'redux-form-validators';
import { toUTC } from '../../../../helpers/dates';
import UserAutoComplete from '../../../common/UserAutoComplete';

const styles = {
    textField: {
        floatingLabelStyle: { fontSize: '12px' },
    },
};

const emptyLimitation = {
    maxDraught: undefined,
    maxBeam: undefined,
    maxLOA: undefined,
    maxDWT: undefined,
    channelDepth: undefined,
    anchorageDepth: undefined,
};

export const futureDateOnly = (value) => {
    if (!value) return undefined;
    const d = value instanceof Date ? value : new Date(value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d.getTime() <= today.getTime() ? 'Select a future date' : undefined;
};

const validateContactPerson = (val) => {
    if (!val) return undefined;
    if (!val._id && val.email) return 'Select user from list';
    if (val.email && val.email.length < 3) return 'User email must be at least 3 characters long';
    return undefined;
};

function renderRecommendedEndAt({ input, meta, ...rest }) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const base = () => (input.value ? new Date(input.value) : new Date());

    const addMonths = (n) => {
        const d = base();
        d.setMonth(d.getMonth() + n);
        input.onChange(d);
    };

    const addYears = (n) => {
        const d = base();
        d.setFullYear(d.getFullYear() + n);
        input.onChange(d);
    };

    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end', 
        }}
        >
            <DatePicker
                {...rest}
                value={input.value ? toUTC(input.value) : undefined}
                onChange={(_e, date) => input.onChange(date)}
                onBlur={() => input.onBlur(input.value)}
                shouldDisableDate={dateSelected => dateSelected.getTime() <= today.getTime()}
                errorText={meta.touched && meta.error}
                textFieldStyle={{ fontSize: '13px' }}
                minDate={today}
            />
            <RaisedButton 
                label="+1 month" 
                type="button" 
                labelStyle={{ fontSize: '12px', padding: 0 }} 
                style={{ marginTop: '16px' }}
                onClick={() => addMonths(1)} 
            />
            <RaisedButton 
                label="+1 year" 
                type="button" 
                labelStyle={{ fontSize: '12px', padding: 0 }} 
                style={{ marginTop: '16px' }}
                onClick={() => addYears(1)} 
            />
            {input.value ? (
                <IconButton
                    title="Remove recommended"
                    style={{
                        width: 32, height: 32, padding: 0, marginTop: '16px', 
                    }}
                    iconStyle={{ color: '#d32f2f' }}
                    onClick={() => { 
                        input.onChange({ endAt: null, at: null, addedBy: null });
                        input.onBlur(null);
                    }}
                >
                    <ContentRemoveIcon />
                </IconButton>
            ) : null}
        </div>
    );
}

function renderCompanyNameAutoComplete({
    input, meta, change, companyIdPath, contactPersonPath,
}) {
    return (
        <CompaniesAutoComplete
            customValue={input.value || ''}
            onTextChange={(val) => {
                input.onChange(val);
                change(companyIdPath, undefined);
                change(contactPersonPath, { _id: '', email: '' });
            }}
            onCompanySelected={(val) => {
                const name = val && val.name ? val.name : '';
                const item = val && val.item ? val.item : null;
                const _id = item && item._id ? item._id : undefined;
                input.onChange(name);
                change(companyIdPath, _id ? { _id, name } : null);
            }}
            floatingLabelText='Company name'
            floatingLabelStyle={styles.textField.floatingLabelStyle}
            errorText={meta.touched && meta.error}
        />
    );
}

function renderContactUserAutoComplete({
    input, meta, change, field, companyFields,
}) {
    const onUserSelected = (value) => {
        if (value.company) {
            change(companyFields.companyId, { _id: value.company._id, name: value.company.name });
            change(companyFields.name, value.company.name);
        }

        change(`${field}`, { _id: value._id, email: value.email });
    };

    const onUserChanging = (value) => {
        change(`${field}`, { _id: '', email: value });
    };
    
    return (
        <UserAutoComplete 
            customValue={
                input.value && typeof input.value === 'object' ?
                    input.value.email || ''
                    : input.value || ''
            }
            onUserSelected={(val) => {
                const item = val && val.item ? val.item : null;
                const email = item && item.name ? item.name : '';
                const _id = item && item._id ? item._id : undefined;

                change(field, (_id || email) ? { _id, email } : null);

                if (item) {
                    onUserSelected(item);
                }
            }}
            onUserChanging={onUserChanging}
            textFieldStyle={{ height: '72px' }}
            inputStyle={{ marginTop: '12px' }}
            // errorText={errors}
            hintText='Contact person'
            floatingLabelText='Contact person'
            withExtendedData
            errorText={meta.error ? meta.error : null}
        />
    );
}

function renderPortLimitations({ fields }) {
    return (
        <div>
            {fields.map((field, limitationIndex) => (
                <div className="row" key={`limitation_${limitationIndex}`}>
                    <div className="col-xs-12">
                        <Field 
                            component={TextField} 
                            name={`${field}.berth`} 
                            floatingLabelText='Berth' 
                            floatingLabelStyle={styles.textField.floatingLabelStyle} 
                            style={{ width: '14%' }} 
                            validate={[required()]}
                        />
                        <Field 
                            component={TextField} 
                            name={`${field}.maxDraught`} 
                            floatingLabelText='Max draft, m:' 
                            floatingLabelStyle={styles.textField.floatingLabelStyle} 
                            style={{ width: '14%', marginLeft: '10px' }} 
                            validate={[numericality({ '>=': 0, '<=': 100, allowBlank: true })]}
                        />
                        <Field 
                            component={TextField} 
                            name={`${field}.maxBeam`} 
                            floatingLabelText='Max beam, m:' 
                            floatingLabelStyle={styles.textField.floatingLabelStyle} 
                            style={{ width: '14%', marginLeft: '10px' }} 
                            validate={[numericality({ '>=': 0, '<=': 100, allowBlank: true })]} 
                        />
                        <Field 
                            component={TextField} 
                            name={`${field}.maxLOA`} 
                            floatingLabelText='Max LOA, m:' 
                            floatingLabelStyle={styles.textField.floatingLabelStyle} 
                            style={{ width: '14%', marginLeft: '10px' }} 
                            validate={[numericality({ '>=': 0, '<=': 1000, allowBlank: true })]} 
                        />
                        <Field 
                            component={TextField} 
                            name={`${field}.maxDWT`} 
                            floatingLabelText='Max DWT, m:' 
                            floatingLabelStyle={styles.textField.floatingLabelStyle} 
                            style={{ width: '14%', marginLeft: '10px' }} 
                            validate={[numericality({ '>=': 0, '<=': 1000000, allowBlank: true })]} 
                        />
                        <Field 
                            component={TextField} 
                            name={`${field}.channelDepth`} 
                            floatingLabelText='Channel Depth, m:' 
                            floatingLabelStyle={styles.textField.floatingLabelStyle} 
                            style={{ width: '14%', marginLeft: '10px' }} 
                            validate={[numericality({ '>=': 0, '<=': 100, allowBlank: true })]} 
                        />
                        <div className='center-xs' style={{ display: 'inline-flex', width: '8%' }}>
                            {limitationIndex === 0 ? (
                                <IconButton onClick={() => fields.push(emptyLimitation)}><ControlPointIcon /></IconButton>
                            ) : (
                                <IconButton onClick={() => fields.remove(limitationIndex)}><ContentRemoveIcon /></IconButton>
                            )}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function ServiceProviderFormFields({
    field, serviceProvider, formChange, hasRecommended, 
}) {
    const [_hasRecommended, setHasRecommended] = useState(hasRecommended);

    useEffect(() => {
        if (!serviceProvider) return;
        const activities = Array.isArray(serviceProvider.activities) ? serviceProvider.activities : [];
        const hasPortAgency = activities.includes('Port Agency');
        if (hasPortAgency) return;

        const rec = serviceProvider.recommended;
        const alreadyCleared = !rec || ((rec.endAt == null) && (rec.at == null) && (rec.addedBy == null));
        if (!alreadyCleared) {
            formChange(`${field}.recommended`, { endAt: null, at: null, addedBy: null });
        }
    }, [field, formChange, serviceProvider && serviceProvider.activities, serviceProvider && serviceProvider.recommended]);
    
    useEffect(() => {
        setHasRecommended(hasRecommended);
    }, [hasRecommended]);
    
    const canBeRecommended = () => {
        if (!hasRecommended) {
            return true;
        }

        if (_hasRecommended._id === serviceProvider._id) {
            return true;
        }

        return false;
    };

    return (
        <React.Fragment>
            <div className="row">
                <div className="col-xs-6">
                    <Field
                        component={SelectField}
                        name={`${field}.activities`}
                        floatingLabelText='Activities'
                        floatingLabelStyle={styles.textField.floatingLabelStyle}
                        multiple
                        value={serviceProvider && serviceProvider.activities}
                        fullWidth
                        autoWidth
                        validate={required()}
                        onChange={(_event, _index, value) => {
                            const activities = Array.isArray(value) ? value : [];
                            if (!activities.includes('Port Agency')) {
                                formChange(`${field}.recommended`, { endAt: null, at: null, addedBy: null });
                            }
                        }}
                    >
                        {portActivities.map((portActivity, index) => (
                            <MenuItem key={index} value={portActivity} primaryText={portActivity} />
                        ))}
                    </Field>
                </div>
            </div>
            <div className='row'>
                <div className='col-xs-6'>
                    <Field 
                        name={`${field}.contactPersonId`}
                        component={renderContactUserAutoComplete}
                        validate={[validateContactPerson]}
                        props={{
                            change: formChange,
                            field: `${field}.contactPersonId`,
                            companyFields: {
                                companyId: `${field}.companyId`,
                                name: `${field}.name`,
                            },
                        }}
                    />
                </div>
                <div className="col-xs-6">
                    <Field
                        name={`${field}.name`}
                        component={renderCompanyNameAutoComplete}
                        validate={[required(), length({ min: 3, max: 266 })]}
                        props={{
                            change: formChange,
                            companyIdPath: `${field}.companyId`,
                            contactPersonPath: `${field}.contactPersonId`,
                            currentCompanyId: serviceProvider && serviceProvider.companyId,
                        }}
                    />
                    <Field name={`${field}.companyId`} component="input" type="hidden" />
                </div>
            </div>

            {serviceProvider && serviceProvider.activities && serviceProvider.activities.includes('Stevedoring Company') && (
                <FieldArray name={`${field}.limitations`} component={renderPortLimitations} />
            )}

            <div className="row">
                <div className="col-xs-6"><FieldArray name={`${field}.emails`} component={Emails} /></div>
                <div className="col-xs-6"><FieldArray name={`${field}.phones`} component={Phones} /></div>
            </div>

            <div className="row">
                <div className="col-xs-6">
                    <Field component={TextField} name={`${field}.address`} floatingLabelText='Address' floatingLabelStyle={styles.textField.floatingLabelStyle} fullWidth validate={required()} />
                </div>
                <div className="col-xs-6">
                    <Field component={TextField} name={`${field}.PIC`} floatingLabelText='PIC' floatingLabelStyle={styles.textField.floatingLabelStyle} fullWidth validate={required()} />
                </div>
            </div>

            {
                serviceProvider && 
                serviceProvider.activities &&
                Array.isArray(serviceProvider.activities) &&
                canBeRecommended() &&
                serviceProvider.activities.includes('Port Agency') ?
                    <div className="row">
                        <div className="col-xs-5" />
                        <div className="col-xs-7">
                            <Field
                                name={`${field}.recommended.endAt`}
                                component={renderRecommendedEndAt}
                                floatingLabelText='Recommended until'
                                floatingLabelStyle={styles.textField.floatingLabelStyle}
                                fullWidth
                                validate={[futureDateOnly]}
                            />
                        </div>
                    </div> : 
                    null

            }
        </React.Fragment>
    );
}



// WEBPACK FOOTER //
// ./app/components/Regions/Port/ServiceProviders/FormFields.jsx